import { useEffect, useMemo } from 'react';
/* eslint-disable no-nested-ternary */
import { ArrowDownOutlined, DeleteOutlined } from '@ant-design/icons';
import { useIsMutating } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getBooleanAnswerOptions } from '@app/constants';
import { hasAllowedRole } from '@app/utils';
import { Namespace } from '@data';
import {
  AnswerType,
  QuestionDependencyView,
  DependencyType,
  QuestionView,
  QuestionDependencyBody,
  Role,
} from '@data/sdk/match';
import {
  Button,
  AntdForm,
  AntdInput,
  AntdSelect,
  AntdTypography,
  AntdCheckbox,
} from '@ui/components';
import { VectorArrow } from '@ui/icons';
import { useQuestionsList } from '../../hooks';
import { useDependencySource } from './hooks';
import { BooleanDependencyFormValues } from './types';
import { getBooleanFormInitialValues, getBooleanFormInitialValuesFromQuestion } from './utils';

const { Text } = AntdTypography;

interface BooleanDependencyFormProps {
  answerType?: AnswerType;
  dependency?: QuestionDependencyView;
  onCancel: () => void;
  onDelete?: () => void;
  onSave: (formValues: QuestionDependencyBody) => void;
  questionText: string;
  similarQuestionDependency?: QuestionView;
}

export const BooleanDependencyForm = ({
  questionText,
  dependency,
  onDelete,
  onCancel,
  answerType,
  onSave,
  similarQuestionDependency,
}: BooleanDependencyFormProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const { questionId = '' } = useParams<{ questionId: string }>();
  const isMutating = !!useIsMutating();
  const IS_REVIEWER = hasAllowedRole([Role.Reviewer]);

  const [form] = AntdForm.useForm<BooleanDependencyFormValues>();
  const onlyIfUserHasDisease = AntdForm.useWatch('onlyIfUserHasDisease', form);

  const initialValues =
    dependency && answerType
      ? getBooleanFormInitialValues(dependency, answerType)
      : similarQuestionDependency
      ? getBooleanFormInitialValuesFromQuestion(similarQuestionDependency)
      : undefined;

  const { questionOptions, loadMoreQuestions, handleSearchChange, questionsLoading } =
    useQuestionsList([AnswerType.Boolean, AnswerType.Options]);

  // To prevent dependency loop
  const filteredQuestionOptions = useMemo(() => {
    return questionOptions.filter((q) => q.value !== questionId);
  }, [questionOptions]);

  const { isOptionsSource, sourceOptionsLoading, applicableAnswerOptions, onSourceSelect } =
    useDependencySource(form, !!dependency);

  const handleApplicableAnswerChange = (value: string) => {
    // An options source has no "other" value to flip to — not-applicable always falls back
    // to the target's boolean false.
    const notApplicableValue =
      onlyIfUserHasDisease || !isOptionsSource
        ? getBooleanAnswerOptions().find((v) => v.value !== value)
        : getBooleanAnswerOptions().find((v) => v.value === 'false');
    form.setFieldValue('notApplicableAnswer', notApplicableValue);
  };

  const handleCreateDependency = (formValues: BooleanDependencyFormValues) => {
    const isHasDisease = formValues.onlyIfUserHasDisease;
    const createDependencyValues: QuestionDependencyBody = {
      sourceId: formValues.source.value,
      parameters: isHasDisease
        ? [formValues.applicableAnswer || '', formValues.defaultValue]
        : [formValues.applicableAnswer, formValues.defaultValue],
      type: isHasDisease ? DependencyType.HasDisease : DependencyType.Dependency,
    };

    onSave(createDependencyValues);
  };

  const handleScroll = (e: any) => {
    if (e.target.scrollTop + e.target.offsetHeight + 2 > e.target.scrollHeight) {
      loadMoreQuestions();
    }
  };

  useEffect(() => {
    form.setFieldValue('source', initialValues?.source);
  }, [similarQuestionDependency]);

  return (
    <div className="flex flex-col gap-7 items-center w-full">
      {onDelete && (
        <Button
          icon={<DeleteOutlined />}
          danger
          ghost
          type="text"
          className="ml-auto"
          onClick={onDelete}
          disabled={isMutating || IS_REVIEWER}
        >
          {t('question_page.details.dependencies.delete_dependence')}
        </Button>
      )}
      <AntdForm
        form={form}
        layout="vertical"
        autoComplete="off"
        requiredMark={false}
        onFinish={handleCreateDependency}
        className="w-full"
        initialValues={initialValues}
      >
        <div className="flex flex-col gap-1 items-center">
          <div className="flex flex-col">
            <Text>{t('question_page.details.dependencies.select_question')}:</Text>
            <AntdForm.Item
              name="source"
              rules={[{ required: true, message: t('patient:validations.please_select') }]}
              className="mb-0"
            >
              <AntdSelect
                showSearch
                placeholder={t('patient:common.type_here')}
                filterOption={false}
                allowClear
                className="min-w-[360px] w-fit"
                options={filteredQuestionOptions}
                onSearch={handleSearchChange}
                onPopupScroll={handleScroll}
                optionLabelProp="label"
                loading={questionsLoading}
                onChange={(_, o) => form.setFieldValue('source', o)}
                onSelect={onSourceSelect}
                disabled={!!dependency}
              />
            </AntdForm.Item>
          </div>

          <div className="flex flex-col gap-1 items-center mt-4">
            <AntdForm.Item name="onlyIfUserHasDisease" valuePropName="checked" className="mb-0">
              <AntdCheckbox disabled={!!dependency}>
                <Text>{t('question_page.details.dependencies.only_if_user_has_disease')}</Text>
              </AntdCheckbox>
            </AntdForm.Item>
          </div>

          <div className="flex flex-nowrap gap-10">
            <div className="flex flex-col items-end w-fit">
              <div className="flex flex-col items-center">
                <ArrowDownOutlined className="text-[28px] text-success" />
                <Text className="text-success">
                  {onlyIfUserHasDisease
                    ? t('question_page.details.dependencies.has_disease_and_applicable_answer')
                    : t('question_page.details.dependencies.applicable_answer')}
                </Text>

                <div className="flex p-4 bg-base-100 rounded-default border-solid border-base-300 w-full">
                  <AntdForm.Item
                    name="applicableAnswer"
                    rules={
                      onlyIfUserHasDisease
                        ? []
                        : [{ required: true, message: t('patient:validations.please_select') }]
                    }
                    noStyle
                  >
                    <AntdSelect
                      filterOption={false}
                      notFoundContent={null}
                      loading={isOptionsSource && sourceOptionsLoading}
                      options={
                        onlyIfUserHasDisease ? getBooleanAnswerOptions() : applicableAnswerOptions
                      }
                      className="min-w-[120px]"
                      placeholder={
                        onlyIfUserHasDisease
                          ? t('question_page.details.dependencies.optional_applicable_answer')
                          : undefined
                      }
                      allowClear={onlyIfUserHasDisease}
                      onChange={handleApplicableAnswerChange}
                    />
                  </AntdForm.Item>
                </div>
              </div>
              <VectorArrow className="text-success transform translate-x-[-50%]" />

              <div className="w-full flex flex-col gap-2 mr-auto  items-center">
                <Text className="text-base-600">
                  {t('question_page.details.dependencies.question_will_be_asked')}
                </Text>
                <div className="p-4 bg-base-100 rounded-default border-solid border-primary-600 w-[400px]">
                  <Text className="text-xl font-medium">{questionText}</Text>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-start w-fit">
              <div className="flex flex-col items-center">
                <ArrowDownOutlined className="text-[28px] text-error" />
                <Text className="text-error">
                  {onlyIfUserHasDisease
                    ? t(
                        'question_page.details.dependencies.does_not_have_disease_or_not_applicable',
                      )
                    : t('question_page.details.dependencies.not_applicable_answer')}
                </Text>

                <div className="flex p-4 bg-base-100 rounded-default border-solid border-base-300 w-full">
                  <AntdForm.Item
                    name="notApplicableAnswer"
                    rules={[{ required: true, message: t('patient:validations.please_select') }]}
                    noStyle
                    dependencies={['applicableAnswer']}
                  >
                    <AntdSelect
                      options={getBooleanAnswerOptions()}
                      className="min-w-[120px]"
                      disabled={!onlyIfUserHasDisease}
                    />
                  </AntdForm.Item>
                </div>
              </div>
              <VectorArrow className="text-error transform translate-x-1/2 scale-x-[-1]" />

              <div className="w-full flex flex-col gap-2 mr-auto  items-center">
                <Text className="text-base-600">
                  {t('question_page.details.dependencies.question_will_not_be_asked')}
                </Text>
                <div className="p-4 bg-base-100 rounded-default border-solid border-primary-600 w-[400px]">
                  <Text className="text-xl font-medium">{questionText}</Text>
                  <div className="flex flex-col">
                    <Text>
                      {t('question_page.details.dependencies.default_value_if_not_applicable')}
                    </Text>
                    <AntdForm.Item
                      name="defaultValue"
                      className="mb-1"
                      rules={[{ required: true, message: t('patient:validations.required') }]}
                    >
                      {answerType === AnswerType.Boolean ? (
                        <AntdSelect options={getBooleanAnswerOptions()} />
                      ) : (
                        <AntdInput className="w-full" />
                      )}
                    </AntdForm.Item>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-base-100 py-2 w-full flex items-center justify-center gap-4 border-solid border-base-300 border-0 border-t mt-6">
          <Button type="primary" htmlType="submit" loading={isMutating} disabled={IS_REVIEWER}>
            {t('patient:common.save_changes')}
          </Button>
          <Button onClick={onCancel} disabled={isMutating}>
            {t('patient:common.cancel')}
          </Button>
        </div>
      </AntdForm>
    </div>
  );
};
